import axios from 'axios';

import { refreshAccessToken } from './refresh';
import { getAccessToken } from './token-store';

/**
 * Renews the access token and replays the request when a plain axios call comes
 * back 401.
 *
 * Apollo has its own retry link, but a good deal of this app does not go through
 * Apollo: uploads, likes, admin calls and resource-asset downloads are direct
 * axios requests. With a 15-minute access token those would simply start failing
 * a quarter of an hour into every session - the kind of breakage that shows up
 * as "the upload button randomly stops working".
 *
 * Retries once. A second 401 after a successful renewal means the session is
 * genuinely gone, and looping would hammer the API against a permanent failure.
 */

const RETRY_FLAG = '__authRetried';

export function installAuthInterceptor(): number {
  return axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const config = error?.config;

      if (status !== 401 || !config || config[RETRY_FLAG]) {
        return Promise.reject(error);
      }

      // The refresh endpoint answering 401 must not trigger another refresh.
      if (typeof config.url === 'string' && config.url.includes('/api/auth/')) {
        return Promise.reject(error);
      }

      config[RETRY_FLAG] = true;

      try {
        await refreshAccessToken();
      } catch {
        // Session is gone. Surface the original 401 rather than the refresh
        // failure, so callers see the error they were expecting.
        return Promise.reject(error);
      }

      config.headers = {
        ...(config.headers ?? {}),
        Authorization: `Bearer ${getAccessToken()}`,
      };

      return axios(config);
    },
  );
}
